import {
  SftpAccountRecordSchema,
  SftpAccountWithSecretSchema,
  SftpDownloadUrlResponseSchema,
  SftpListResponseSchema,
  SftpUploadUrlResponseSchema,
  type CreateSftpAccountDto,
  type RotateSftpAccountDto,
  type SftpAccountRecord,
  type SftpAccountWithSecret,
  type SftpDownloadUrlResponse,
  type SftpListResponse,
  type SftpUploadUrlRequest,
  type SftpUploadUrlResponse,
  type UpdateSftpAccountDto,
} from "@lumika/shared";
import { z } from "zod";
import { apiFetch } from "./client";

const SftpAccountListSchema = z.array(SftpAccountRecordSchema);

export const sftpApi = {
  /** Lists every SFTP credential on the platform. Credentials are
   *  global — they're not tied to a dealership (see CreateSftpAccountDto). */
  listAccounts: (): Promise<SftpAccountRecord[]> =>
    apiFetch("/sftp/accounts", SftpAccountListSchema),

  getAccount: (id: string): Promise<SftpAccountRecord> =>
    apiFetch(`/sftp/accounts/${id}`, SftpAccountRecordSchema),

  createAccount: (body: CreateSftpAccountDto): Promise<SftpAccountWithSecret> =>
    apiFetch("/sftp/accounts", SftpAccountWithSecretSchema, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** `body.password` optional — when omitted the server auto-generates. */
  rotateAccount: (
    id: string,
    body: RotateSftpAccountDto = {},
  ): Promise<SftpAccountWithSecret> =>
    apiFetch(`/sftp/accounts/${id}/rotate`, SftpAccountWithSecretSchema, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateAccount: (id: string, body: UpdateSftpAccountDto): Promise<SftpAccountRecord> =>
    apiFetch(`/sftp/accounts/${id}`, SftpAccountRecordSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  removeAccount: (id: string): Promise<void> =>
    apiFetch(`/sftp/accounts/${id}`, z.undefined(), { method: "DELETE" }) as Promise<void>,

  listFiles: (accountId: string, path?: string): Promise<SftpListResponse> => {
    const qs = path ? `?path=${encodeURIComponent(path)}` : "";
    return apiFetch(`/sftp/accounts/${accountId}/files${qs}`, SftpListResponseSchema);
  },

  createUploadUrl: (
    accountId: string,
    body: SftpUploadUrlRequest,
  ): Promise<SftpUploadUrlResponse> =>
    apiFetch(`/sftp/accounts/${accountId}/files/upload-url`, SftpUploadUrlResponseSchema, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createDownloadUrl: (accountId: string, path: string): Promise<SftpDownloadUrlResponse> =>
    apiFetch(
      `/sftp/accounts/${accountId}/files/download-url?path=${encodeURIComponent(path)}`,
      SftpDownloadUrlResponseSchema,
    ),

  deletePath: (accountId: string, path: string): Promise<{ path: string }> =>
    apiFetch(
      `/sftp/accounts/${accountId}/files/delete`,
      z.object({ path: z.string() }),
      { method: "POST", body: JSON.stringify({ path }) },
    ),

  createFolder: (accountId: string, path: string): Promise<{ path: string }> =>
    apiFetch(
      `/sftp/accounts/${accountId}/files/folders`,
      z.object({ path: z.string() }),
      { method: "POST", body: JSON.stringify({ path }) },
    ),

  // --- Global "all folders" browser ----------------------------------
  // Backed by the bucket-wide /sftp/files surface. Same shape as the
  // per-account endpoints but `path` is the absolute bucket key.

  listGlobal: (path?: string): Promise<SftpListResponse> => {
    const qs = path ? `?path=${encodeURIComponent(path)}` : "";
    return apiFetch(`/sftp/files${qs}`, SftpListResponseSchema);
  },

  createUploadUrlGlobal: (
    body: SftpUploadUrlRequest,
  ): Promise<SftpUploadUrlResponse> =>
    apiFetch(`/sftp/files/upload-url`, SftpUploadUrlResponseSchema, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createDownloadUrlGlobal: (path: string): Promise<SftpDownloadUrlResponse> =>
    apiFetch(
      `/sftp/files/download-url?path=${encodeURIComponent(path)}`,
      SftpDownloadUrlResponseSchema,
    ),

  deletePathGlobal: (path: string): Promise<{ path: string }> =>
    apiFetch(
      `/sftp/files/delete`,
      z.object({ path: z.string() }),
      { method: "POST", body: JSON.stringify({ path }) },
    ),

  createFolderGlobal: (path: string): Promise<{ path: string }> =>
    apiFetch(
      `/sftp/files/folders`,
      z.object({ path: z.string() }),
      { method: "POST", body: JSON.stringify({ path }) },
    ),
};
